import AdminLayout from "@/components/AdminLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useState } from "react";
import { toast } from "@/hooks/use-toast";
import { Fuel, Ship, MapPin, Key, Settings } from "lucide-react";

const AdminCrewIntelligence = () => {
  // API Configuration
  const [oilPriceApiKey, setOilPriceApiKey] = useState("");
  const [fredApiKey, setFredApiKey] = useState("");
  const [jetPriceMT, setJetPriceMT] = useState("855");
  const [mgoPriceMT, setMgoPriceMT] = useState("650");

  // AIRCAT Parameters
  const [aircatMCR, setAircatMCR] = useState("1625");
  const [aircatMaintHr, setAircatMaintHr] = useState("190");
  const [aircatPax, setAircatPax] = useState("80");

  // Helicopter defaults
  const [heliData, setHeliData] = useState([
    { key: "S92", name: "Sikorsky S-92A", pax: 19, kts: 145, burn: 510, maint: 35000 },
    { key: "AW139", name: "Leonardo AW139", pax: 12, kts: 139, burn: 285, maint: 28000 },
    { key: "H175", name: "Airbus H175", pax: 16, kts: 142, burn: 390, maint: 30000 },
    { key: "AS332", name: "Airbus AS332L2", pax: 19, kts: 130, burn: 430, maint: 32000 },
    { key: "AW189", name: "Leonardo AW189", pax: 16, kts: 144, burn: 380, maint: 30000 },
    { key: "H160", name: "Airbus H160", pax: 12, kts: 130, burn: 230, maint: 25000 },
    { key: "S76D", name: "Sikorsky S-76D", pax: 12, kts: 145, burn: 205, maint: 22000 },
  ]);

  // Block/field data (read-only display for now)
  const blocks = [
    { name: "Stabroek Block, Guyana", nm: 114, effKts: 39, avail: 98.1 },
    { name: "Block 58, Suriname", nm: 120, effKts: 39, avail: 97.8 },
    { name: "French Guiana (Emerging)", nm: 140, effKts: 39, avail: 97.5 },
    { name: "Pre-Salt Campos Basin, Brazil", nm: 130, effKts: 39, avail: 95.8 },
    { name: "Pre-Salt Santos Basin, Brazil", nm: 200, effKts: 38, avail: 94.5 },
    { name: "Deepwater GoM (MC/GC), USA", nm: 160, effKts: 41, avail: 93.5 },
    { name: "Norwegian North Sea", nm: 150, effKts: 37, avail: 80.5 },
    { name: "UK Central North Sea", nm: 180, effKts: 37, avail: 81.2 },
    { name: "Angola Block 32", nm: 200, effKts: 39, avail: 93.5 },
    { name: "Nigeria Deepwater (OML 130)", nm: 130, effKts: 41, avail: 97.0 },
    { name: "Mozambique Rovuma", nm: 80, effKts: 40, avail: 95.0 },
    { name: "UAE / Qatar — Persian Gulf", nm: 80, effKts: 45, avail: 99.0 },
    { name: "Malaysia Deepwater (Sabah)", nm: 120, effKts: 42, avail: 97.2 },
    { name: "NW Shelf, Australia", nm: 200, effKts: 37, avail: 85.0 },
  ];

  const handleSaveFuel = () => {
    toast({ title: "Fuel pricing saved", description: "Fuel price parameters have been updated." });
  };

  const handleSaveVessel = () => {
    toast({ title: "Vessel parameters saved", description: "AIRCAT 35 parameters have been updated." });
  };

  const handleSaveHeli = () => {
    toast({ title: "Helicopter parameters saved", description: "Helicopter fleet data has been updated." });
  };

  const handleSaveApi = () => {
    toast({ title: "API configuration saved", description: "OilPriceAPI and FRED API keys have been updated." });
  };

  const updateHeli = (idx: number, field: string, value: string) => {
    setHeliData(prev => prev.map((h, i) => i === idx ? { ...h, [field]: field === "name" ? value : parseFloat(value) || 0 } : h));
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-display font-bold text-primary-foreground">Crew Transfer Intelligence</h1>
          <p className="text-sm text-primary-foreground/50 mt-1">Configure fuel pricing API, vessel parameters, helicopter fleet, and operating block distances.</p>
        </div>

        <Tabs defaultValue="api" className="space-y-4">
          <TabsList className="bg-primary-foreground/5 border border-primary-foreground/10">
            <TabsTrigger value="api" className="data-[state=active]:bg-teal/20 data-[state=active]:text-teal gap-1.5"><Key className="w-3.5 h-3.5" /> API Config</TabsTrigger>
            <TabsTrigger value="fuel" className="data-[state=active]:bg-teal/20 data-[state=active]:text-teal gap-1.5"><Fuel className="w-3.5 h-3.5" /> Fuel Prices</TabsTrigger>
            <TabsTrigger value="vessel" className="data-[state=active]:bg-teal/20 data-[state=active]:text-teal gap-1.5"><Ship className="w-3.5 h-3.5" /> Vessel & Heli</TabsTrigger>
            <TabsTrigger value="blocks" className="data-[state=active]:bg-teal/20 data-[state=active]:text-teal gap-1.5"><MapPin className="w-3.5 h-3.5" /> Blocks & Fields</TabsTrigger>
          </TabsList>

          {/* API Configuration */}
          <TabsContent value="api">
            <Card className="bg-primary-foreground/[0.03] border-primary-foreground/10">
              <CardHeader>
                <CardTitle className="text-primary-foreground text-lg">API Configuration</CardTitle>
                <CardDescription className="text-primary-foreground/50">Configure external API keys for live fuel pricing data.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-primary-foreground/70 text-xs font-medium">OilPriceAPI Key (MGO Pricing)</Label>
                  <Input
                    type="password"
                    placeholder="Enter your OilPriceAPI key..."
                    value={oilPriceApiKey}
                    onChange={e => setOilPriceApiKey(e.target.value)}
                    className="bg-primary-foreground/5 border-primary-foreground/10 text-primary-foreground placeholder:text-primary-foreground/30"
                  />
                  <p className="text-[11px] text-primary-foreground/40">Used for live MGO port pricing. Get a key at <span className="text-teal">oilpriceapi.com</span></p>
                </div>
                <div className="space-y-2">
                  <Label className="text-primary-foreground/70 text-xs font-medium">FRED API Key (Jet A-1 Pricing)</Label>
                  <Input
                    type="password"
                    placeholder="Enter your FRED API key..."
                    value={fredApiKey}
                    onChange={e => setFredApiKey(e.target.value)}
                    className="bg-primary-foreground/5 border-primary-foreground/10 text-primary-foreground placeholder:text-primary-foreground/30"
                  />
                  <p className="text-[11px] text-primary-foreground/40">Used for live Jet A-1 spot pricing (series DJFUELUSGULF). Get a key at <span className="text-teal">fred.stlouisfed.org</span></p>
                </div>
                <Button onClick={handleSaveApi} size="sm" className="bg-teal hover:bg-teal/90 text-white">Save API Keys</Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Fuel Pricing */}
          <TabsContent value="fuel">
            <Card className="bg-primary-foreground/[0.03] border-primary-foreground/10">
              <CardHeader>
                <CardTitle className="text-primary-foreground text-lg">Fuel Price Defaults</CardTitle>
                <CardDescription className="text-primary-foreground/50">Fallback fuel prices used when API data is unavailable. All prices in $/MT.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-primary-foreground/70 text-xs font-medium">Jet A-1 ($/MT)</Label>
                    <Input
                      type="number"
                      value={jetPriceMT}
                      onChange={e => setJetPriceMT(e.target.value)}
                      className="bg-primary-foreground/5 border-primary-foreground/10 text-primary-foreground font-mono"
                    />
                    <p className="text-[10px] text-primary-foreground/40">Aviation kerosene — helicopter fuel</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-primary-foreground/70 text-xs font-medium">MGO ($/MT)</Label>
                    <Input
                      type="number"
                      value={mgoPriceMT}
                      onChange={e => setMgoPriceMT(e.target.value)}
                      className="bg-primary-foreground/5 border-primary-foreground/10 text-primary-foreground font-mono"
                    />
                    <p className="text-[10px] text-primary-foreground/40">Marine Gas Oil — AIRCAT 35 fuel</p>
                  </div>
                </div>
                <Button onClick={handleSaveFuel} size="sm" className="bg-teal hover:bg-teal/90 text-white">Save Fuel Prices</Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Vessel & Helicopter Parameters */}
          <TabsContent value="vessel" className="space-y-4">
            {/* AIRCAT */}
            <Card className="bg-primary-foreground/[0.03] border-primary-foreground/10">
              <CardHeader>
                <CardTitle className="text-primary-foreground text-lg">AIRCAT 35 Parameters</CardTitle>
                <CardDescription className="text-primary-foreground/50">Core performance parameters for the AIRCAT 35 CTV model.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-primary-foreground/70 text-xs font-medium">MCR Fuel Burn (L/hr)</Label>
                    <Input type="number" value={aircatMCR} onChange={e => setAircatMCR(e.target.value)} className="bg-primary-foreground/5 border-primary-foreground/10 text-primary-foreground font-mono" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-primary-foreground/70 text-xs font-medium">Maintenance ($/hr)</Label>
                    <Input type="number" value={aircatMaintHr} onChange={e => setAircatMaintHr(e.target.value)} className="bg-primary-foreground/5 border-primary-foreground/10 text-primary-foreground font-mono" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-primary-foreground/70 text-xs font-medium">Passenger Capacity</Label>
                    <Input type="number" value={aircatPax} onChange={e => setAircatPax(e.target.value)} className="bg-primary-foreground/5 border-primary-foreground/10 text-primary-foreground font-mono" />
                  </div>
                </div>
                <Button onClick={handleSaveVessel} size="sm" className="bg-teal hover:bg-teal/90 text-white mt-4">Save AIRCAT Parameters</Button>
              </CardContent>
            </Card>

            {/* Helicopter Fleet */}
            <Card className="bg-primary-foreground/[0.03] border-primary-foreground/10">
              <CardHeader>
                <CardTitle className="text-primary-foreground text-lg">Helicopter Fleet Parameters</CardTitle>
                <CardDescription className="text-primary-foreground/50">Modify performance and cost data for each helicopter type used in comparisons.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-primary-foreground/10 text-[11px] text-primary-foreground/50 uppercase tracking-wider">
                        <th className="text-left py-2 pr-3">Helicopter</th>
                        <th className="text-center py-2 px-2">Pax</th>
                        <th className="text-center py-2 px-2">Speed (kts)</th>
                        <th className="text-center py-2 px-2">Burn (kg/hr)</th>
                        <th className="text-center py-2 px-2">Maint ($/day)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {heliData.map((h, i) => (
                        <tr key={h.key} className="border-b border-primary-foreground/5">
                          <td className="py-2 pr-3 text-primary-foreground/80 font-medium">{h.name}</td>
                          <td className="py-2 px-1"><Input type="number" value={h.pax} onChange={e => updateHeli(i, "pax", e.target.value)} className="bg-primary-foreground/5 border-primary-foreground/10 text-primary-foreground font-mono text-center h-8 w-16 mx-auto" /></td>
                          <td className="py-2 px-1"><Input type="number" value={h.kts} onChange={e => updateHeli(i, "kts", e.target.value)} className="bg-primary-foreground/5 border-primary-foreground/10 text-primary-foreground font-mono text-center h-8 w-16 mx-auto" /></td>
                          <td className="py-2 px-1"><Input type="number" value={h.burn} onChange={e => updateHeli(i, "burn", e.target.value)} className="bg-primary-foreground/5 border-primary-foreground/10 text-primary-foreground font-mono text-center h-8 w-20 mx-auto" /></td>
                          <td className="py-2 px-1"><Input type="number" value={h.maint} onChange={e => updateHeli(i, "maint", e.target.value)} className="bg-primary-foreground/5 border-primary-foreground/10 text-primary-foreground font-mono text-center h-8 w-20 mx-auto" /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Button onClick={handleSaveHeli} size="sm" className="bg-teal hover:bg-teal/90 text-white mt-4">Save Helicopter Parameters</Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Blocks & Fields */}
          <TabsContent value="blocks">
            <Card className="bg-primary-foreground/[0.03] border-primary-foreground/10">
              <CardHeader>
                <CardTitle className="text-primary-foreground text-lg">Operating Blocks & Field Distances</CardTitle>
                <CardDescription className="text-primary-foreground/50">Distance parameters, sea-state adjusted speeds, and operational availability for each offshore block.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-primary-foreground/10 text-[11px] text-primary-foreground/50 uppercase tracking-wider">
                        <th className="text-left py-2 pr-3">Block / Field</th>
                        <th className="text-center py-2 px-3">Distance (nm)</th>
                        <th className="text-center py-2 px-3">Eff. Speed (kts)</th>
                        <th className="text-center py-2 px-3">Availability (%)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {blocks.map((b, i) => (
                        <tr key={i} className="border-b border-primary-foreground/5">
                          <td className="py-2.5 pr-3 text-primary-foreground/80 font-medium">{b.name}</td>
                          <td className="py-2.5 text-center font-mono text-primary-foreground/70">{b.nm}</td>
                          <td className="py-2.5 text-center font-mono text-primary-foreground/70">{b.effKts}</td>
                          <td className="py-2.5 text-center font-mono text-primary-foreground/70">{b.avail}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-[11px] text-primary-foreground/40 mt-3 italic">Block parameters are derived from metocean data and AIRCAT TA-202601 technical analysis. Contact development to modify block data.</p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default AdminCrewIntelligence;
